/**
 * StepBodySkeleton
 *
 * Renders a skeleton that mirrors the real step body while the initial
 * application fetch is in flight. Inlined by `OnboardingFlow` in place of
 * the concrete step component — all surrounding chrome (brand, step rail,
 * header, footer nav) renders its real content immediately.
 *
 * Per-step variants mirror the actual rendered layout so the swap into the
 * loaded step causes no layout shift:
 *
 *   0  Business Type      — 2-col grid of 6 option cards + info notice
 *   1  Personal Info      — 2-col name row, email, DOB, SSN, address section
 *   2  Business Info      — 3 single inputs + info notice
 *   3  Restaurant Details — concept, contact email, hours editor (7 days)
 *   4  Owners             — 2 roster cards + add-owner button
 *   5  Review             — 4 review sections
 */

import { Skeleton } from '@order-up/common';

export interface StepBodySkeletonProps {
  stepIndex: number;
}

export function StepBodySkeleton({ stepIndex }: StepBodySkeletonProps) {
  switch (stepIndex) {
    case 1:
      return <PersonalInfoSkeleton />;
    case 2:
      return <BusinessInfoSkeleton />;
    case 3:
      return <LocationDetailsSkeleton />;
    case 4:
      return <OwnersSkeleton />;
    case 5:
      return <ReviewSkeleton />;
    case 0:
    default:
      return <BusinessTypeSkeleton />;
  }
}

function LocationDetailsSkeleton() {
  return (
    <div className="space-y-8">
      <FieldSkeleton labelWidth="w-40" />
      <FieldSkeleton labelWidth="w-44" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-32" />
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded-lg" />
        ))}
      </div>
    </div>
  );
}

function BusinessTypeSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="flex items-start gap-4 rounded-xl border border-border bg-zinc-900/50 p-4"
          >
            <Skeleton className="mt-0.5 h-5 w-5 rounded-full" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          </div>
        ))}
      </div>

      {/* Restricted industries notice — static in real step, rendered real */}
      <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
        <p className="text-sm text-amber-200/80">
          <span className="font-medium text-amber-300">Note:</span>
          {' '}We aren't able to support businesses in certain industries including cryptocurrency, marijuana-related
          companies, money service businesses, and gambling businesses.
        </p>
      </div>
    </div>
  );
}

function PersonalInfoSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <FieldSkeleton labelWidth="w-20" />
        <FieldSkeleton labelWidth="w-20" />
      </div>
      <FieldSkeleton labelWidth="w-28" />
      <FieldSkeleton labelWidth="w-28" />
      <FieldSkeleton labelWidth="w-40" />

      <div className="space-y-4">
        <Skeleton className="h-6 w-36" />
        <FieldSkeleton labelWidth="w-16" />
        <div className="grid gap-4 sm:grid-cols-2">
          <FieldSkeleton labelWidth="w-10" />
          <FieldSkeleton labelWidth="w-12" />
        </div>
        <FieldSkeleton labelWidth="w-20" />
      </div>
    </div>
  );
}

function BusinessInfoSkeleton() {
  return (
    <div className="space-y-6">
      <FieldSkeleton labelWidth="w-36" />
      <FieldSkeleton labelWidth="w-24" />
      <FieldSkeleton labelWidth="w-28" />
      <Skeleton className="h-16 w-full rounded-lg" />
    </div>
  );
}

function OwnersSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border bg-zinc-900/40 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-5 w-5 rounded" />
          </div>
          <Skeleton className="h-3 w-48" />
          <Skeleton className="h-3 w-32" />
        </div>
      ))}
      <Skeleton className="h-11 w-full rounded-lg" />
    </div>
  );
}

function ReviewSkeleton() {
  return (
    <div className="space-y-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border bg-zinc-900/40 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-16" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, j) => (
              <div key={j} className="space-y-1.5">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-4 w-40" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FieldSkeleton({ labelWidth }: { labelWidth: string }) {
  return (
    <div className="space-y-2">
      <Skeleton className={`h-4 ${labelWidth}`} />
      <Skeleton className="h-11 w-full rounded-lg" />
    </div>
  );
}
